import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, Building2, Clock, Pencil, FileText, CheckCircle2, AlertCircle, Star, Tag } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useClient, useUpdateClient } from "@/hooks/useClients";
import { useClientPaymentStats } from "@/hooks/useClientPaymentStats";
import { useProjects } from "@/hooks/useProjects";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { z } from "zod";
import { getRelationshipStatusLabel, getRelationshipStatusColor } from "@/lib/client-calculations";
import { ClientAgendaSection } from "@/components/clients/ClientAgendaSection";

const statusColors = {
  backlog: "bg-muted text-muted-foreground",
  in_progress: "bg-primary/10 text-primary",
  review: "bg-warning/10 text-warning",
  done: "bg-success/10 text-success",
};

const statusLabels = {
  backlog: "Backlog",
  in_progress: "Em Andamento",
  review: "Revisão",
  done: "Concluído",
};

// Schema de validação
const clientSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório").max(100),
  email: z.string().email("Email inválido").max(255),
  phone: z.string().max(20).optional().or(z.literal("")),
  company: z.string().max(100).optional().or(z.literal("")),
  hourly_rate: z.string().optional().or(z.literal("")),
  notes: z.string().max(1000).optional().or(z.literal("")),
  relationship_status: z.string().optional(),
  rating: z.string().optional().or(z.literal("")),
  tags: z.string().optional().or(z.literal("")),
});

interface ClientFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  hourly_rate: string;
  notes: string;
  relationship_status: string;
  rating: string;
  tags: string;
}

// Máscara de telefone brasileiro
function formatPhoneBR(value: string): string {
  const numbers = value.replace(/\D/g, "").slice(0, 11);
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
}

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { data: client, isLoading: isLoadingClient } = useClient(id || "");
  const { data: allProjects } = useProjects();
  const { data: clientPaymentStats } = useClientPaymentStats(id || "");
  const updateClient = useUpdateClient();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState<ClientFormData>({
    name: "",
    email: "",
    phone: "",
    company: "",
    hourly_rate: "",
    notes: "",
    relationship_status: "ativo",
    rating: "",
    tags: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Filtrar projetos do cliente
  const clientProjects = allProjects?.filter((p) => p.client_id === id) || [];
  const projectIds = clientProjects.map(p => p.id);

  // Buscar pagamentos do cliente
  const { data: clientPayments } = useQuery({
    queryKey: ['payments', 'client', id],
    queryFn: async () => {
      if (projectIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .in('project_id', projectIds)
        .order('due_date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: projectIds.length > 0,
  });

  // Buscar dados de revenue detalhados dos projetos do cliente
  const { data: projectRevenueData } = useQuery({
    queryKey: ['project-revenue', 'client', id],
    queryFn: async () => {
      if (projectIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('project_revenue')
        .select('*')
        .in('id', projectIds);

      if (error) throw error;
      return data || [];
    },
    enabled: projectIds.length > 0,
  });

  // Estatísticas de pagamento
  const totalPaid = clientPaymentStats?.totalPaid || 0;
  const totalPending = clientPaymentStats?.totalPending || 0;
  const totalOverdue = clientPaymentStats?.totalOverdue || 0;
  const totalPayments = totalPaid + totalPending + totalOverdue;
  const paymentProgress = totalPayments > 0 ? (totalPaid / totalPayments) * 100 : 0;
  const hourlyRate = client?.hourly_rate || 0;

  // Histórico de pagamentos por mês
  const paymentHistoryArray = useMemo(() => {
    if (!clientPayments || clientPayments.length === 0) return [];

    const history = clientPayments.reduce((acc, payment) => {
      // Usar due_date ou paid_date para agrupar
      const dateStr = payment.paid_date || payment.due_date;
      if (!dateStr) return acc;
      
      const date = new Date(dateStr);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      
      if (!acc[monthKey]) {
        acc[monthKey] = { monthKey, month: monthLabel, paid: 0, pending: 0, overdue: 0 };
      }
      
      const amount = Number(payment.amount) || 0;
      if (payment.status === 'paid') {
        acc[monthKey].paid += amount;
      } else if (payment.status === 'pending') {
        acc[monthKey].pending += amount;
      } else if (payment.status === 'overdue') {
        acc[monthKey].overdue += amount;
      }
      
      return acc;
    }, {} as Record<string, { monthKey: string; month: string; paid: number; pending: number; overdue: number }>);

    return Object.values(history)
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey))
      .slice(0, 6);
  }, [clientPayments]);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        email: client.email,
        phone: client.phone || "",
        company: client.company || "",
        hourly_rate: client.hourly_rate?.toString() || "",
        notes: client.notes || "",
        relationship_status: (client as any).relationship_status || "ativo",
        rating: (client as any).rating?.toString() || "",
        tags: ((client as any).tags || []).join(", "),
      });
    }
  }, [client]);

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({ ...prev, phone: formatPhoneBR(value) }));
  };

  const handleSaveClient = async () => {
    if (!client) return;

    const result = clientSchema.safeParse(formData);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          errors[err.path[0].toString()] = err.message;
        }
      });
      setFormErrors(errors);
      return;
    }

    if (formData.hourly_rate && parseFloat(formData.hourly_rate) < 0) {
      setFormErrors({ hourly_rate: "Valor deve ser maior ou igual a 0" });
      return;
    }

    try {
      // Parse tags from comma-separated string
      const tagsArray = formData.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      // Parse rating
      const ratingValue = formData.rating ? parseInt(formData.rating) : null;
      if (ratingValue !== null && (ratingValue < 1 || ratingValue > 5)) {
        setFormErrors({ rating: "Avaliação deve ser entre 1 e 5" });
        return;
      }

      await updateClient.mutateAsync({
        id: client.id,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        company: formData.company.trim() || null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        notes: formData.notes.trim() || null,
        relationship_status: formData.relationship_status || 'ativo',
        rating: ratingValue,
        tags: tagsArray,
      } as any);

      toast({
        title: "Sucesso!",
        description: "Cliente atualizado com sucesso.",
      });
      setIsEditDialogOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o cliente.",
      });
    }
  };

  if (isLoadingClient) {
    return (
      <DashboardLayout title="Carregando...">
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-48 w-full" />
          <div className="grid gap-6 lg:grid-cols-2">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout title="Cliente não encontrado">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">O cliente solicitado não foi encontrado.</p>
          <Button onClick={() => navigate("/clients")}>Voltar para clientes</Button>
        </div>
      </DashboardLayout>
    );
  }

  const initials = client.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <DashboardLayout title="Detalhes do Cliente">
      <div className="space-y-6">
        {/* Back Link */}
        <Link
          to="/clients"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para clientes
        </Link>

        {/* Client Header */}
        <Card className="shadow-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-3 sm:gap-4">
                <Avatar className="h-12 w-12 sm:h-16 sm:w-16">
                  <AvatarFallback className="bg-primary/10 text-primary text-lg sm:text-xl font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-1 sm:space-y-2 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl sm:text-2xl font-bold truncate">{client.name}</h2>
                    <Badge className={getRelationshipStatusColor((client as any).relationship_status || 'ativo')}>
                      {getRelationshipStatusLabel((client as any).relationship_status || 'ativo')}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => setIsEditDialogOpen(true)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* Rating */}
                  {(client as any).rating && (
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${
                            star <= ((client as any).rating || 0)
                              ? 'fill-warning text-warning'
                              : 'text-muted-foreground/30'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="h-4 w-4 shrink-0" />
                      <span className="truncate">{client.email}</span>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 shrink-0" />
                        {client.phone}
                      </div>
                    )}
                    {client.company && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 shrink-0" />
                        {client.company}
                      </div>
                    )}
                  </div>
                  {/* Tags */}
                  {((client as any).tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {((client as any).tags || []).map((tag: string, index: number) => (
                        <Badge key={index} variant="outline" className="text-xs gap-1">
                          <Tag className="h-3 w-3" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-4 sm:gap-6">
                <div className="text-center">
                  <div className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-success">
                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />
                    R$ {totalPaid.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Recebido</p>
                </div>
                {totalPending > 0 && (
                  <div className="text-center">
                    <div className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-warning">
                      <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                      R$ {totalPending.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Pendente</p>
                  </div>
                )}
                {totalOverdue > 0 && (
                  <div className="text-center">
                    <div className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-destructive">
                      <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                      R$ {totalOverdue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Atrasado</p>
                  </div>
                )}
                {hourlyRate > 0 && (
                  <div className="text-center">
                    <div className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-muted-foreground">
                      R$ {hourlyRate.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/h
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Valor/Hora</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notas */}
            {client.notes && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>{client.notes}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agenda Section */}
        <ClientAgendaSection 
          clientId={id || ""} 
          projects={clientProjects.map(p => ({
            id: p.id,
            name: p.name,
            deadline: p.deadline,
            status: p.status,
          }))} 
        />

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Projects */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">Projetos ({clientProjects.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {clientProjects.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum projeto vinculado a este cliente.
                </p>
              ) : (
                <div className="space-y-4">
                  {clientProjects.map((project) => {
                    // Buscar dados da view project_revenue
                    const projectRevenue = projectRevenueData?.find(pr => pr.id === project.id);
                    const projectHours = Number(projectRevenue?.hours_spent) || 0;
                    const projectValue = Number(projectRevenue?.revenue) || 0;
                    const billingType = project.billing_type || 'hourly';
                    
                    return (
                      <Link
                        key={project.id}
                        to={`/projects/${project.id}`}
                        className="block rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{project.name}</h4>
                              <Badge variant="outline" className="text-xs">
                                {billingType === 'fixed' ? 'Preço Fixo' : 'Por Hora'}
                              </Badge>
                            </div>
                            <Badge className={statusColors[project.status || 'backlog']}>
                              {statusLabels[project.status || 'backlog']}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{Math.round(projectHours)}h</p>
                            <p className="text-sm text-success">
                              R$ {projectValue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">Histórico de Pagamentos</CardTitle>
            </CardHeader>
            <CardContent>
              {paymentHistoryArray.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum pagamento registrado ainda.
                </p>
              ) : (
                <div className="space-y-4">
                  {paymentHistoryArray.map((item, index) => (
                    <div
                      key={index}
                      className="border-b pb-4 last:border-0 last:pb-0"
                    >
                      <p className="font-medium capitalize mb-2">{item.month}</p>
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        {item.paid > 0 && (
                          <div className="flex items-center gap-1 text-success">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            R$ {item.paid.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                          </div>
                        )}
                        {item.pending > 0 && (
                          <div className="flex items-center gap-1 text-warning">
                            <Clock className="h-3.5 w-3.5" />
                            R$ {item.pending.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                          </div>
                        )}
                        {item.overdue > 0 && (
                          <div className="flex items-center gap-1 text-destructive">
                            <AlertCircle className="h-3.5 w-3.5" />
                            R$ {item.overdue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog/Drawer */}
      {isMobile ? (
        <Drawer open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DrawerContent className="max-h-[90vh]">
            <DrawerHeader className="text-left">
              <DrawerTitle>Editar Cliente</DrawerTitle>
              <DrawerDescription>
                Atualize as informações do cliente
              </DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-4 overflow-y-auto">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name-mobile">Nome *</Label>
                  <Input 
                    id="edit-name-mobile" 
                    placeholder="Nome da empresa ou pessoa"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={formErrors.name ? "border-destructive" : ""}
                  />
                  {formErrors.name && <p className="text-sm text-destructive">{formErrors.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email-mobile">Email *</Label>
                  <Input 
                    id="edit-email-mobile" 
                    type="email" 
                    placeholder="email@empresa.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={formErrors.email ? "border-destructive" : ""}
                  />
                  {formErrors.email && <p className="text-sm text-destructive">{formErrors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone-mobile">Telefone</Label>
                  <Input 
                    id="edit-phone-mobile" 
                    placeholder="(11) 99999-9999"
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-hourly_rate-mobile">Valor Hora (R$)</Label>
                  <Input 
                    id="edit-hourly_rate-mobile" 
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="100.00"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                    className={formErrors.hourly_rate ? "border-destructive" : ""}
                  />
                  {formErrors.hourly_rate && <p className="text-sm text-destructive">{formErrors.hourly_rate}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-company-mobile">Empresa</Label>
                  <Input 
                    id="edit-company-mobile" 
                    placeholder="Nome da empresa (opcional)"
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-notes-mobile">Notas</Label>
                  <Textarea 
                    id="edit-notes-mobile" 
                    placeholder="Observações sobre o cliente..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-relationship_status-mobile">Status do Relacionamento</Label>
                  <select
                    id="edit-relationship_status-mobile"
                    value={formData.relationship_status}
                    onChange={(e) => setFormData(prev => ({ ...prev, relationship_status: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                    <option value="prospect">Prospect</option>
                    <option value="perdido">Perdido</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-rating-mobile">Avaliação (1-5)</Label>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          rating: prev.rating === star.toString() ? "" : star.toString() 
                        }))}
                        className="p-1 hover:bg-muted rounded"
                      >
                        <Star
                          className={`h-5 w-5 ${
                            parseInt(formData.rating || "0") >= star
                              ? 'fill-warning text-warning'
                              : 'text-muted-foreground/30'
                          }`}
                        />
                      </button>
                    ))}
                    {formData.rating && (
                      <span className="text-sm text-muted-foreground ml-2">
                        {formData.rating}/5
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-tags-mobile">Tags</Label>
                  <Input 
                    id="edit-tags-mobile" 
                    placeholder="Ex: VIP, Recorrente, Novo (separadas por vírgula)"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">Separe as tags por vírgula</p>
                </div>
              </div>
            </div>
            <DrawerFooter className="pt-2">
              <Button 
                onClick={handleSaveClient} 
                disabled={updateClient.isPending}
              >
                {updateClient.isPending ? "Salvando..." : "Salvar Alterações"}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Editar Cliente</DialogTitle>
              <DialogDescription>
                Atualize as informações do cliente
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome *</Label>
                <Input 
                  id="edit-name" 
                  placeholder="Nome da empresa ou pessoa"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={formErrors.name ? "border-destructive" : ""}
                />
                {formErrors.name && <p className="text-sm text-destructive">{formErrors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email *</Label>
                <Input 
                  id="edit-email" 
                  type="email" 
                  placeholder="email@empresa.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={formErrors.email ? "border-destructive" : ""}
                />
                {formErrors.email && <p className="text-sm text-destructive">{formErrors.email}</p>}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Telefone</Label>
                  <Input 
                    id="edit-phone" 
                    placeholder="(11) 99999-9999"
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-hourly_rate">Valor Hora (R$)</Label>
                  <Input 
                    id="edit-hourly_rate" 
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="100.00"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                    className={formErrors.hourly_rate ? "border-destructive" : ""}
                  />
                  {formErrors.hourly_rate && <p className="text-sm text-destructive">{formErrors.hourly_rate}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-company">Empresa</Label>
                <Input 
                  id="edit-company" 
                  placeholder="Nome da empresa (opcional)"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notas</Label>
                <Textarea 
                  id="edit-notes" 
                  placeholder="Observações sobre o cliente..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="edit-relationship_status">Status do Relacionamento</Label>
                  <select
                    id="edit-relationship_status"
                    value={formData.relationship_status}
                    onChange={(e) => setFormData(prev => ({ ...prev, relationship_status: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                    <option value="prospect">Prospect</option>
                    <option value="perdido">Perdido</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-rating">Avaliação</Label>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          rating: prev.rating === star.toString() ? "" : star.toString() 
                        }))}
                        className="p-1 hover:bg-muted rounded"
                      >
                        <Star
                          className={`h-5 w-5 ${
                            parseInt(formData.rating || "0") >= star
                              ? 'fill-warning text-warning'
                              : 'text-muted-foreground/30'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-tags">Tags</Label>
                <Input 
                  id="edit-tags" 
                  placeholder="Ex: VIP, Recorrente, Novo (separadas por vírgula)"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Separe as tags por vírgula</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveClient} 
                disabled={updateClient.isPending}
              >
                {updateClient.isPending ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </DashboardLayout>
  );
}
